import { z } from 'zod'

import { apiRequest } from '@/api/client'

const vendorSchema = z.object({
  id: z.number(),
  username: z.string(),
  email: z.string(),
  full_name: z.string(),
  whatsapp_number: z.string().nullable().optional(),
  business_name: z.string().nullable().optional(),
  role: z.literal('vendor').or(z.string()),
  is_active: z.boolean(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
})

export type VendorAccount = z.infer<typeof vendorSchema>

export type VendorUpsertPayload = {
  username: string
  email: string
  full_name: string
  whatsapp_number: string
  business_name?: string | null
  is_active?: boolean
}

const passwordResetSchema = z.object({
  vendor_id: z.number(),
  email: z.string(),
  emailed: z.boolean(),
  reset_url: z.string().nullable().optional(),
  expires_hours: z.number(),
})

export type PasswordResetResult = z.infer<typeof passwordResetSchema>

export function fetchAdminVendors() {
  return apiRequest('/api/v1/vendors/admin', (data) =>
    z.object({ vendors: z.array(vendorSchema) }).parse(data),
  )
}

export function createAdminVendor(payload: VendorUpsertPayload) {
  return apiRequest('/api/v1/vendors/admin', (data) => z.object({ vendor: vendorSchema }).parse(data), {
    method: 'POST',
    body: payload,
  })
}

export function updateAdminVendor(id: number, payload: VendorUpsertPayload) {
  return apiRequest(`/api/v1/vendors/admin/${id}`, (data) => z.object({ vendor: vendorSchema }).parse(data), {
    method: 'PATCH',
    body: payload,
  })
}

export function setAdminVendorActive(id: number, is_active: boolean) {
  return apiRequest(
    `/api/v1/vendors/admin/${id}/active`,
    (data) => z.object({ vendor: vendorSchema }).parse(data),
    {
      method: 'PATCH',
      body: { is_active },
    },
  )
}

export function sendAdminVendorPasswordReset(id: number) {
  return apiRequest(`/api/v1/vendors/admin/${id}/password-reset`, (data) => passwordResetSchema.parse(data), {
    method: 'POST',
    body: {},
  })
}

export function completePasswordReset(token: string, password: string) {
  return apiRequest(
    '/api/v1/auth/reset-password',
    (data) => z.object({ ok: z.boolean(), email: z.string().optional() }).parse(data),
    {
      method: 'POST',
      body: { token, password },
      authenticated: false,
    },
  )
}
